import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { useSaveClinicNotesMutation } from "app/store/transmission/api/transmissionNotesApi";

import {
  onChangeClinicNotes,
  setClinicNotesTouched,
  onReportFINChange,
  onSpeechRecognizing,
  selectedReport,
  toggleSubmitting,
} from "app/store/transmission/slice/transmissionSlice";
import useNotesFooter from "../footer/useNotesFooter";
import { SUBMIT_ACTIONS } from "static/constants";
import { showErrorToast, showSuccessToast } from "utils/toast";
import useDebounce from "app/hooks/useDebounce";
import useTranscription from "hooks/useTranscription";

const useClinicNotes = (data) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    reportId,
    clinicNotesExpanded,
    clinicNotesTouched,
    clinicNoteSubmitting,
    clinicNoteSubmittingAndProcessing,
    clinicNoteSubmittingAndSigningAndProcessing,
    clinicNoteSubmittingAndSigningAndUnProcessing,
    clinicNotesEsig,
    clinicNotes,
    originalClinicNotes,
    reportFIN,
    originalReportFIN,
    clinicNotesEsigDisabled,
    isLegacyReport,
    reportDetail,
    alertStatus,
    attentionStatus,
    isSpeechRecognizing,
  } = useSelector((state) => state.transmission);

  const { handleProcess } = useNotesFooter(data);

  const clinicNotesRef = useRef(clinicNotes);

  useEffect(() => {
    if (data?.clinic_notes) {
      const [note, fin] = data?.clinic_notes?.split("\n\nFIN: ") || [];
      dispatch(
        onChangeClinicNotes({
          note: note?.trim(),
          notesTouched: false,
          isOriginal: true,
        })
      );

      dispatch(
        onReportFINChange({
          fin: fin?.trim(),
          notesTouched: false,
          isOriginal: true,
        })
      );
    }
  }, [data?.clinic_notes, dispatch]);

  useEffect(() => {
    if (clinicNotes) {
      const inputId = document.getElementById("clinic-note");
      if (inputId && isSpeechRecognizing) {
        inputId.focus();
        inputId.setSelectionRange(clinicNotes.length, clinicNotes.length);
        inputId.scrollLeft = inputId.scrollWidth;
      }
    }
  }, [clinicNotes, isSpeechRecognizing]);

  const [newClinicNote, setNewClinicNote] = useState("");

  const debounceDispatch = useCallback(
    useDebounce((value) => {
      dispatch(
        onChangeClinicNotes({
          note: value,
          notesTouched: true,
        })
      );
    }, 500),
    []
  );

  const handleInputChange = async (updatedClinicNote) => {
    setNewClinicNote(updatedClinicNote);
    if (!clinicNotesTouched) dispatch(setClinicNotesTouched(true));
    debounceDispatch(updatedClinicNote);
  };

  useEffect(() => {
    setNewClinicNote(clinicNotes);
  }, [clinicNotes]);

  useEffect(() => {
    clinicNotesRef.current = clinicNotes || "";
  }, [clinicNotes]);

  const handleTranscript = useCallback(
    (transcript) => {
      const currentNotes = clinicNotesRef.current;
      const updated = currentNotes
        ? `${currentNotes} ${transcript}`
        : transcript;

      setNewClinicNote(updated);
      clinicNotesRef.current = updated;

      dispatch(
        onChangeClinicNotes({
          note: updated,
          notesTouched: true,
        })
      );
    },
    [dispatch]
  );

  const { isTranscribing, startTranscription, stopTranscription } =
    useTranscription({
      onStart: () => dispatch(onSpeechRecognizing(true)),
      onStop: () => dispatch(onSpeechRecognizing(false)),
      onError: (message) => showErrorToast(message),
      onTranscript: handleTranscript,
    });

  const toggleSpeechRecognition = useCallback(() => {
    if (isTranscribing) {
      stopTranscription();
      return;
    }

    startTranscription();
  }, [isTranscribing, startTranscription, stopTranscription]);

  const [saveClinicNotes] = useSaveClinicNotesMutation();

  const onSaveClinicNotes = async (
    isProcessAction = false,
    isSignAction = false,
    processState = "process"
  ) => {
    const submitAction = isProcessAction
      ? isSignAction
        ? processState === "process"
          ? SUBMIT_ACTIONS.SAVE_SIGN_AND_PROCESS
          : SUBMIT_ACTIONS.SAVE_SIGN_AND_UNPROCESS
        : SUBMIT_ACTIONS.SAVE_AND_PROCESS
      : SUBMIT_ACTIONS.SAVE;

    try {
      dispatch(
        toggleSubmitting({ type: "clinicNotes", value: true, submitAction })
      );

      const result = await saveClinicNotes({
        reportId,
        clinicNotes,
        eSig: isSignAction ? true : clinicNotesEsig,
        reportFIN,
        physicianNotes: "",
      }).unwrap();

      if (result) {
        dispatch(toggleSubmitting({ type: "clinicNotes", value: false }));
        if (!isProcessAction) showSuccessToast(result?.message);

        const clinicSignatureData = {
          name: result?.clinicSignatureUpdates?.updated_by,
          updated_at: result?.clinicSignatureUpdates?.updated_at,
        };
        const clinicNotes = result?.report?.[0]?.clinic_notes || "";
        dispatch(
          selectedReport({
            id: reportId,
            isLegacyReport: isLegacyReport,
            reportDetail: {
              ...reportDetail,
              clinic_notes: clinicNotes,
              alert_flag: alertStatus,
              warning_flag: attentionStatus,
              clinicSignature: clinicSignatureData,
            },
          })
        );

        // The saved text holds the note and FIN together; keep them apart as on load
        const [note, fin] = clinicNotes.split("\n\nFIN: ");
        dispatch(
          onChangeClinicNotes({
            note: note?.trim(),
            notesTouched: false,
            isOriginal: true,
          })
        );
        dispatch(
          onReportFINChange({
            fin: fin?.trim() || "",
            notesTouched: false,
            isOriginal: true,
          })
        );

        if (isProcessAction) {
          await handleProcess(processState, isSignAction);
        }
      }
    } catch (err) {
      showErrorToast(err?.message);
    } finally {
      dispatch(
        toggleSubmitting({ type: "clinicNotes", value: false, submitAction })
      );
      await stopTranscription();
    }
  };

  const cancelClinicNotes = () => {
    // Drop a keystroke still waiting in the debounce, or it lands after Cancel and re-applies the edit.
    debounceDispatch.cancel();
    setNewClinicNote(originalClinicNotes);
    dispatch(
      onChangeClinicNotes({
        note: originalClinicNotes,
        notesTouched: false,
        isOriginal: true,
      })
    );
    dispatch(
      onReportFINChange({ fin: originalReportFIN, notesTouched: false })
    );

    stopTranscription();
  };

  return {
    theme,
    dispatch,
    reportFIN,
    clinicNotes,
    reportDetail,
    newClinicNote,
    handleInputChange,
    flushClinicNote: debounceDispatch.flush,
    speechRecognition: null,
    cancelClinicNotes,
    onSaveClinicNotes,
    clinicNotesTouched,
    isSpeechRecognizing,
    originalClinicNotes,
    clinicNotesExpanded,
    clinicNoteSubmitting,
    clinicNotesEsigDisabled,
    toggleSpeechRecognition,
    clinicNoteSubmittingAndProcessing,
    clinicNoteSubmittingAndSigningAndProcessing,
    clinicNoteSubmittingAndSigningAndUnProcessing,
  };
};

export default useClinicNotes;
